/**
 * RetentionWarningBanner Component (US-071)
 *
 * A warning banner that displays when a job or application is approaching
 * its retention deletion date. This gives users advance notice to export
 * data if needed before automatic deletion.
 *
 * Designed to be shown on:
 * - Job Detail page when a job is expiring soon
 * - Application Detail page when an application is expiring soon
 * - Candidate Detail page when their data is expiring
 *
 * Warning thresholds:
 * - 7+ days: Info notice (blue)
 * - 3-6 days: Warning notice (amber)
 * - 1-2 days: Urgent notice (red)
 */
import { AlertTriangle, Clock, Download, ExternalLink, X } from 'lucide-react';
import { useState, ReactNode } from 'react';
import { Button } from '../ui/Button';

export type WarningUrgency = 'info' | 'warning' | 'urgent';

export interface RetentionWarningBannerProps {
  /** Number of days until deletion */
  daysRemaining: number;
  /** Type of entity being warned about */
  entityType: 'job' | 'application' | 'candidate';
  /** Name/title of the entity for display */
  entityName?: string;
  /** Count of related items (e.g., applications for a job) */
  relatedCount?: number;
  /** Type of related items (e.g., "applications" for a job) */
  relatedType?: string;
  /** Deletion date for display */
  deletionDate?: Date | string;
  /** Whether the banner can be dismissed */
  dismissible?: boolean;
  /** Callback when banner is dismissed */
  onDismiss?: () => void;
  /** Export data action handler */
  onExportData?: () => void;
  /** Custom action button */
  action?: ReactNode;
  /** Link to retention settings */
  settingsLink?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Determine the urgency level based on days remaining
 */
function getUrgencyLevel(daysRemaining: number): WarningUrgency {
  if (daysRemaining <= 2) return 'urgent';
  if (daysRemaining <= 6) return 'warning';
  return 'info';
}

/**
 * Get styling based on urgency level
 */
const urgencyStyles: Record<
  WarningUrgency,
  {
    container: string;
    icon: string;
    title: string;
    text: string;
    iconBg: string;
  }
> = {
  info: {
    container: 'border-blue-200 bg-blue-50',
    icon: 'text-blue-600',
    iconBg: 'bg-blue-100',
    title: 'text-blue-800',
    text: 'text-blue-700',
  },
  warning: {
    container: 'border-amber-200 bg-amber-50',
    icon: 'text-amber-600',
    iconBg: 'bg-amber-100',
    title: 'text-amber-800',
    text: 'text-amber-700',
  },
  urgent: {
    container: 'border-red-200 bg-red-50',
    icon: 'text-red-600',
    iconBg: 'bg-red-100',
    title: 'text-red-800',
    text: 'text-red-700',
  },
};

/**
 * Format the deletion date for display
 */
function formatDeletionDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get the warning message based on entity type and days remaining
 */
function getWarningMessage(
  entityType: 'job' | 'application' | 'candidate',
  daysRemaining: number,
  relatedCount?: number,
  relatedType?: string
): string {
  const timeText =
    daysRemaining === 0
      ? 'today'
      : daysRemaining === 1
      ? 'tomorrow'
      : `in ${daysRemaining} days`;

  switch (entityType) {
    case 'job':
      return relatedCount && relatedCount > 0
        ? `This job and its ${relatedCount} ${relatedType || 'applications'} will be automatically deleted ${timeText} due to the data retention policy.`
        : `This job will be automatically deleted ${timeText} due to the data retention policy.`;

    case 'application':
      return `This application will be automatically deleted ${timeText} due to the data retention policy.`;

    case 'candidate':
      return relatedCount && relatedCount > 0
        ? `This candidate and ${relatedCount} associated ${relatedType || 'applications'} will be automatically deleted ${timeText}.`
        : `This candidate record will be automatically deleted ${timeText} due to the data retention policy.`;

    default:
      return `This data will be automatically deleted ${timeText} due to the data retention policy.`;
  }
}

export function RetentionWarningBanner({
  daysRemaining,
  entityType,
  entityName,
  relatedCount,
  relatedType,
  deletionDate,
  dismissible = false,
  onDismiss,
  onExportData,
  action,
  settingsLink = '/settings',
  className = '',
}: RetentionWarningBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't render if dismissed or if no warning needed (negative days means already past)
  if (isDismissed || daysRemaining < 0) return null;

  const urgency = getUrgencyLevel(daysRemaining);
  const styles = urgencyStyles[urgency];
  const message = getWarningMessage(entityType, daysRemaining, relatedCount, relatedType);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  return (
    <div
      className={`rounded-lg border p-4 ${styles.container} ${className}`}
      role="alert"
      aria-live={urgency === 'urgent' ? 'assertive' : 'polite'}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 rounded-full p-1.5 ${styles.iconBg}`}>
          {urgency === 'urgent' ? (
            <AlertTriangle className={`h-5 w-5 ${styles.icon}`} aria-hidden="true" />
          ) : (
            <Clock className={`h-5 w-5 ${styles.icon}`} aria-hidden="true" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-semibold ${styles.title}`}>
            {urgency === 'urgent'
              ? 'Deletion Imminent'
              : urgency === 'warning'
              ? 'Expiring Soon'
              : 'Scheduled for Deletion'}
            {entityName && (
              <span className="font-normal ml-1">- {entityName}</span>
            )}
          </h4>

          <p className={`mt-1 text-sm ${styles.text}`}>{message}</p>

          {deletionDate && (
            <p className={`mt-1 text-xs ${styles.text} opacity-75`}>
              Scheduled deletion: {formatDeletionDate(deletionDate)}
            </p>
          )}

          {/* Action buttons */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {onExportData && (
              <Button
                variant={urgency === 'urgent' ? 'primary' : 'secondary'}
                size="sm"
                onClick={onExportData}
              >
                <Download className="h-4 w-4 mr-1.5" />
                Export Data
              </Button>
            )}

            {action}

            {settingsLink && (
              <a
                href={settingsLink}
                className={`inline-flex items-center text-sm font-medium ${styles.text} hover:underline`}
              >
                View Retention Settings
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            )}
          </div>
        </div>

        {/* Dismiss button */}
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className={`flex-shrink-0 rounded p-1 ${styles.text} hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2`}
            aria-label="Dismiss warning"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}

RetentionWarningBanner.displayName = 'RetentionWarningBanner';
